/**
 * Theme utilities — hex↔HSL conversion for theme token storage.
 *
 * The Storefront Theme DocType stores color values in HSL component format
 * ("199 89% 48%") without the hsl() wrapper, matching Tailwind's convention.
 * The editor UI uses hex ("#0ea5e9") for color pickers.
 *
 * These converters bridge the two formats.
 */

// ---------------------------------------------------------------------------
// Hex → HSL
// ---------------------------------------------------------------------------

/**
 * Convert hex color to HSL component string.
 * @example hexToHsl("#0ea5e9") → "199 89% 48%"
 */
export function hexToHsl(hex: string): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return '0 0% 0%'
  const [h, s, l] = rgbToHsl(rgb.r, rgb.g, rgb.b)
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`
}

// ---------------------------------------------------------------------------
// HSL → Hex
// ---------------------------------------------------------------------------

/**
 * Convert HSL component string back to hex.
 * @example hslToHex("199 89% 48%") → "#0ea5e9"
 */
export function hslToHex(hsl: string): string {
  const match = hsl.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/)
  if (!match) return '#000000'
  const h = parseFloat(match[1])
  const s = parseFloat(match[2]) / 100
  const l = parseFloat(match[3]) / 100
  const { r, g, b } = hslToRgb(h, s, l)
  return rgbToHex(r, g, b)
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '')
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16)
    const g = parseInt(clean[1] + clean[1], 16)
    const b = parseInt(clean[2] + clean[2], 16)
    return { r, g, b }
  }
  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16)
    const g = parseInt(clean.slice(2, 4), 16)
    const b = parseInt(clean.slice(4, 6), 16)
    return { r, g, b }
  }
  return null
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l * 100]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6
  return [h * 360, s * 100, l * 100]
}

function hslToRgb(
  h: number,
  s: number,
  l: number,
): { r: number; g: number; b: number } {
  if (s === 0) {
    const v = Math.round(l * 255)
    return { r: v, g: v, b: v }
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hNorm = h / 360
  return {
    r: Math.round(hue2rgb(p, q, hNorm + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, hNorm) * 255),
    b: Math.round(hue2rgb(p, q, hNorm - 1 / 3) * 255),
  }
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_GLOBAL_STYLES = {
  brandColor: '#0ea5e9',   // sky-500
  fontFamily: 'system',
  buttonStyle: 'rounded',
  sectionSpacing: 'normal',
  pageMaxWidth: undefined as number | undefined,
}

export const MAX_WIDTH_PRESETS = [
  { value: 1024, label: '1024' },
  { value: 1280, label: '1280' },
  { value: 1440, label: '1440' },
] as const

export const FONT_OPTIONS = [
  { value: 'system', label: 'Default (System)', css: 'system-ui, sans-serif' },
  { value: 'sarabun', label: 'Sarabun', css: "'Sarabun', sans-serif" },
  { value: 'prompt', label: 'Prompt', css: "'Prompt', sans-serif" },
  { value: 'ibm-plex-sans-thai', label: 'IBM Plex Sans Thai', css: "'IBM Plex Sans Thai', sans-serif" },
  { value: 'noto-sans-thai', label: 'Noto Sans Thai', css: "'Noto Sans Thai', sans-serif" },
  { value: 'inter', label: 'Inter', css: "'Inter', sans-serif" },
  { value: 'anuphan', label: 'Anuphan', css: "'Anuphan', sans-serif" },
] as const

export const BUTTON_STYLE_OPTIONS = [
  { value: 'rounded', label: 'Rounded', radius: '16px' },
  { value: 'pill', label: 'Pill', radius: '9999px' },
  { value: 'square', label: 'Square', radius: '0' },
] as const

export const SPACING_OPTIONS = [
  { value: 'compact', label: 'Compact', px: '16px' },
  { value: 'normal', label: 'Normal', px: '32px' },
  { value: 'relaxed', label: 'Relaxed', px: '48px' },
] as const

/** Google Fonts URL for non-system fonts */
export function getGoogleFontUrl(fontKey: string): string | null {
  const map: Record<string, string> = {
    sarabun: 'Sarabun:wght@400;600;700',
    prompt: 'Prompt:wght@400;600;700',
    'ibm-plex-sans-thai': 'IBM+Plex+Sans+Thai:wght@400;600;700',
    'noto-sans-thai': 'Noto+Sans+Thai:wght@400;600;700',
    inter: 'Inter:wght@400;600;700',
    anuphan: 'Anuphan:wght@400;500;600;700',
  }
  const family = map[fontKey]
  if (!family) return null
  return `https://fonts.googleapis.com/css2?family=${family}&display=swap`
}
